<!--suppress JSUnresolvedVariable -->
<template>
  <PopperCard
    v-bind="$attrs"
    :style-class="{ loading: $apollo.loading, error: crash }"
    :placement="placement"
    strategy="fixed"
    @hover="getCard"
  >
    <slot />

    <template #title>
      {{ $apollo.loading ? $t('Loading') : crash ? $t('Error') : card ? card.name : '' }}
    </template>
    <template v-if="card && !crash" #content>
      <div ref="info-card" class="row info-card">
        <div class="col-xs-4 profile-card-left-col">
          <a :href="card.url">
            <img :src="card.picture" class="img-circle m-b-5 profile-card-img" />
          </a>

          <template v-if="isFreelancer">
            <span v-if="card.experience === 'stud'" class="label bgm-yellow c-white student">
              {{ $t('Student') }}
            </span>
            <div class="text-center currency" style="margin: 0 -10px">
              <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
              <CurrencyDisplay
                v-if="card.hourRate || card.dailyRate"
                :show-converted-rates-by-default="showConvertedRatesByDefault"
                :currency-code="card.freelancerRateCurrency"
                :converted-currency-code="user.currency ? user.currency : 'Undefined'"
                :rate="parseInt(card.hourRate || card.dailyRate)"
                :show-currency-code="true"
                :ceiling="true"
              >
                <template #after-rate>
                  {{ card.hourRate ? $t('rate.hourly_slash') : $t('rate.daily_slash') }}
                </template>
              </CurrencyDisplay>
              <small
                v-else
                v-tooltip.top="$t('The freelancer has not chosen to set an hourly rate')"
                class="iblock c-gray"
              >
                {{ $t('Hourly rate is not specified') }}
              </small>
            </div>
            <div class="text-center" style="margin: 0 -15px">
              <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
              <profile-stars
                :stars="card.rating !== null ? card.rating : -1"
                :jobs="card.numTakenJobs"
              />
              <span class="text-muted text-xs">
                {{ card.numTakenJobs }} {{ $t('jobs') }}
                <br />
              </span>
            </div>
          </template>
        </div>
        <div class="col-xs-8">
          <span v-if="isLikeAble && account.isCompany" class="heart">
            <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
            <profile-like
              :liked="card.liked"
              :card-type="cardType"
              :card-id="card.localId"
              :not-liked-color="'c-white'"
            />
          </span>
          <div v-if="(isFreelancer && card.jobTitle) || card.name" class="f-500 c-indigo job-title">
            {{ isFreelancer && card.jobTitle ? card.jobTitle : card.name }}
          </div>
          <!-- Currently team stuff is not in the api, so it is not implemented-->
          <div v-if="card.isTeamMember" style="margin: 2px 0 5px">
            <span class="label bgm-purple c-white">
              {{ $t(card.role) }}
            </span>
          </div>
          <p class="f-12 p-b-15">
            <span v-if="card.city" class="c-gray truncate-info">
              {{ card.city }}
              <br />
            </span>
            <span v-if="card.phone" class="f-500 truncate-info">
              {{ card.phone }}
              <br />
            </span>
            <span v-if="canSeeEmail" class="f-500 truncate-info">
              <a class="tw-break-words" :href="'mailto:' + card.email">{{ card.email }}</a>
            </span>
            <span v-if="card.hasBusinessEntitySettingVisible" class="c-gray truncate-info">
              <br />
              <FreelancerBusinessSetup
                :country="card.country"
                :business-type="card.stringBasedOnBusinessType"
              />
            </span>
          </p>
          <template v-if="isFreelancer">
            <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
            <profile-skills :skill-list="skills" :max="5" :interactive="false" />
            <div v-if="card.clients.length > 0" class="clients p-b-15">
              {{ $t('Divisions') }}:
              <profile-info-card
                v-for="(client, i) in card.clients"
                :key="i"
                :global-id="client.globalId"
              >
                <span class="pointer label label-basic">
                  {{ client.name }}
                </span>
              </profile-info-card>
            </div>
          </template>

          <div v-if="account.isCompany && isFreelancer">
            <div v-if="card.viewerCanContact" class="btn-group">
              <a :href="'/profile/' + card.localId + '/invite'" class="btn btn-xs btn-success">
                {{ $t('Contact') }}
              </a>

              <template v-if="card.dialogs.length > 0">
                <button
                  type="button"
                  class="btn btn-xs btn-success dropdown-toggle"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  <span class="caret" />
                  <span class="sr-only">{{ $t('Toggle Dropdown') }}</span>
                </button>
                <ul class="dropdown-menu dropdown-menu-right f-500 f-12">
                  <li class="text-center">{{ $t('Conversations with') }} {{ card.name }}</li>
                  <li role="separator" class="divider" />
                  <template v-for="dialog in card.dialogs">
                    <!-- eslint-disable-next-line vue/require-v-for-key -- Baseline eslint ignore please fix if working on this-->
                    <li>
                      <a :href="'/messages/' + dialog.localId" class="truncate-dialog">
                        {{
                          dialog.yjob
                            ? dialog.yjob.name
                            : $t('Conversation') + ' ' + formatDate(dialog.updatedAt)
                        }}
                      </a>
                    </li>
                  </template>
                </ul>
              </template>
            </div>
            <a
              v-if="card.viewerCanHire"
              :href="'/profile/' + card.localId + '/hire'"
              class="btn btn-xs btn-default"
            >
              {{ $t('Hire') }}
            </a>
          </div>
          <div v-else-if="card.dialogs.length > 0 && globalId !== account.localId">
            <div class="btn-group">
              <button
                type="button"
                class="btn btn-xs btn-success dropdown-toggle"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                {{ $t('Conversations') }}
                <span class="caret" />
                <span class="sr-only">{{ $t('Toggle Dropdown') }}</span>
              </button>
              <ul class="dropdown-menu dropdown-menu-right f-500 f-12">
                <li class="text-center">{{ $t('Conversations with') }} {{ card.name }}</li>
                <li role="separator" class="divider" />
                <!-- eslint-disable-next-line vue/require-v-for-key -- Baseline eslint ignore please fix if working on this-->
                <li v-for="dialog in card.dialogs">
                  <a :href="'/messages/' + dialog.localId" class="truncate-dialog">
                    {{
                      dialog.yjob
                        ? dialog.yjob.name
                        : $t('Conversation') + ' ' + formatDate(dialog.updatedAt)
                    }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </template>
  </PopperCard>
</template>

<script>
import gql from 'graphql-tag'
import enums from '@/utils/enum'
import { contains, flat } from '@/utils/tools'
import BasePopper from '@/components/BasePopper.vue'
import FunctionZipMixin from '@/mixins/MethodFZipMixin'
import PopperCard from '@/components/PopperCard.vue'
import FormatDateMixin from '@/mixins/MethodFormatDateMixin'
import FreelancerBusinessSetup from '@/components/FreelancerBusinessSetup.vue'

export default {
  name: 'ProfileInfoCard',
  // eslint-disable-next-line vue/no-unused-components -- Baseline eslint ignore please fix if working on this
  components: { BasePopper, PopperCard, FreelancerBusinessSetup },
  mixins: [FunctionZipMixin, FormatDateMixin],
  apollo: {
    card: {
      query: gql`
        query InfoQuery($globalId: ID!, $locale: String!, $currentAccountId: ID!) {
          card(globalId: $globalId) {
            globalId
            localId
            name
            picture
            type: __typename
            ... on Account {
              localId
              rating
              primaryLocation {
                country
                city
              }
            }

            ... on User {
              role: teamRoleForAccount(accountId: $currentAccountId)
            }

            ... on Freelancer {
              viewerCanHire
              viewerCanContact
              clients: viewerSharedClientDivisions {
                globalId
                name
              }
              hourRate
              dailyRate
              currency
              experience
              jobTitle
              numTakenJobs
              skills {
                globalId
                priority
                name(locale: $locale)
              }
              hasBusinessEntitySettingVisible
              stringBasedOnBusinessType
            }

            ... on HasProfile {
              primaryProfile {
                url
              }
            }

            ... on IsLikeable {
              liked
            }

            ... on ContactInformation {
              phone
              email
            }
          }
          me {
            globalId
            activeAccount {
              globalId
              list: dialogs(first: 10, whereAccount: $globalId) {
                # Look at the dialogs between the two accounts
                # Renamed data to dialogs for flattening step
                dialogs: data {
                  globalId
                  localId
                  updatedAt
                  yjob {
                    globalId
                    localId
                    name
                  }
                }
              }
            }
          }
        }
      `,
      variables() {
        return {
          globalId: this.globalId,
          locale: this.user.locale ?? currentLanguageSelected,
          currentAccountId: this.account.globalId,
        }
      },
      manual: true,
      result({ data, loading }) {
        if (loading === 1) {
          return
        } // We are just loading, so skip us

        const isCompany =
          { type: '', ...flat(data.card) }.type.toLowerCase() === enums.accountTypes.company
        // We create our card data
        this.card = {
          // First we setup our standard values
          ...{
            picture: isCompany ? '/gfx/missing-logo.png' : '/gfx/missing-avatar.svg',
            type: '',
            email: null,
            role: null,
            clients: [],
            url: '#', // Profile url, # by default if no url is provided
            viewerCanHire: false,
            viewerCanContact: false,
            isTeamMember: contains(data.card, 'role'),
            dialogs: [],
            freelancerRateCurrency: data.card.currency, // Prevent the override of freelancer currency by me currency
            stringBasedOnBusinessType: null,
          },
          // Here we flatten our data and merged into the card
          // When flattening we simply remove excess depth from our data object so it is easier to work with,
          // and we don't have to worry about running into undefined or null values that might have had
          // child values hidden in them
          ...flat(data.card),
          ...flat(data.me),
        }
      },
      skip: () => true,
      error(e) {
        this.crash = true
        throw e
      },
    },
  },
  props: {
    globalId: {
      type: String,
      required: true,
    },
    placement: {
      type: String,
      default: 'left',
    },
  },
  data() {
    return {
      card: false,
      crash: false,
    }
  },
  computed: {
    isFreelancer() {
      // We lowercase it since __typename usually is capitalized
      return this.card.type.toLowerCase() === enums.accountTypes.freelancer
    },
    skills() {
      return this.card.skills
    },
    cardType() {
      return enums.cards.profile
    },
    canSeeEmail() {
      return this.card.email !== null
    },
    isLikeAble() {
      return this.card.liked !== undefined
    },
    showConvertedRatesByDefault() {
      return Boolean(this.$store?.state?.user?.automatic_currency_conversion)
    },
  },
  methods: {
    getCard() {
      this.$apollo.queries.card.skip = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import 'resources/sass/frontend/variable-overrides';

:deep(.info-card) {
  cursor: default;

  .job-title {
    padding-right: 15px;
  }

  .heart {
    position: absolute;
    top: -50px;
    right: 20px;
  }

  .stars {
    white-space: nowrap;
    transform: scale(0.9);
    // Centers the stars
  }

  .skills {
    display: inline-block;
  }

  .truncate-info {
    display: block;
    width: 13em;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .truncate-dialog {
    display: block;
    width: 20em;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.info-card .stars,
.info-card .currency,
.info-card .label.student {
  text-align: center;
  margin: 5px -10px 0;
}
.profile-card-img {
  max-width: 70px;
}
.profile-card-left-col {
  text-align: center;
}
</style>
